import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CheckoutApi } from "../api";
import { CartData } from "../../../types/cart";
import { CHECKOUT_STATUS, FromConstObject } from "../../../types";
import type {
  GetCheckoutRequest,
  GetCheckoutResponse,
  PatchCheckoutRequest,
  PatchCheckoutResponse,
} from "../../../types/checkout";
import { log } from "../utils/logger";
import { Errors } from "../../../shared-utils/error";
import { useCart } from "../providers/cart";

// Scoped logger for checkout
const logger = log.scope("useCheckoutData");

/**
 * Discriminated union for checkout states
 */
export type CheckoutState =
  | { status: "idle" }
  | { status: "loading" }
  | {
      status: "redirect-success";
      // clientSecret: string;
      data: Extract<GetCheckoutResponse, { success: true }>["data"];
    }
  | {
      status: "error";
      error: Extract<PatchCheckoutResponse, { success: false }>["error"];
      errorInfo?: Extract<
        PatchCheckoutResponse,
        { success: false }
      >["errorInfo"];
      code: FromConstObject<typeof Errors>;
    }
  | {
      status: "ready";
      data: Extract<PatchCheckoutResponse, { success: true }>["data"];
    };

/**
 * Custom hook to manage checkout data fetching and state
 */
export const useCheckoutData = ({} = {}) => {
  const [checkoutState, setCheckoutState] = useState<CheckoutState>({
    status: "idle",
  });
  // SUCCESS PAYMENT REDIRECT LOGIC

  // const [searchParams] = useSearchParams();
  // const payment_intent_client_secret = searchParams.get(
  //   "payment_intent_client_secret"
  // );
  // const redirect_status = searchParams.get("redirect_status");
  // useEffect(() => {
  //   // TODO: TESTING - SHOULD HANDLE REDIRECT BETTER
  //   if (payment_intent_client_secret && redirect_status === "succeeded") {
  //     // SHOULD RESET WHEN ALMOST SUCCEEDED instead of after?
  //     logger.info("Payment succeeded, resetting cart");
  //     setCheckoutState((s) => ({
  //       ...s,
  //       status: "redirect-success",
  //       // clientSecret: payment_intent_client_secret!,
  //     }));
  //     onComplete?.();
  //     logger.info("Handling payment redirect", {
  //       checkoutState,
  //       redirectStatus: redirect_status,
  //     });
  //   }
  // }, [payment_intent_client_secret, redirect_status, cart, onComplete]);

  const patchCheckout = useCallback(
    (request: PatchCheckoutRequest) => {
      setCheckoutState((checkout) => ({ ...checkout, status: "loading" }));

      CheckoutApi.patchCheckout(request)
        .then((response) => {
          if (response.success) {
            setCheckoutState({ status: "ready", data: response.data });
          } else {
            setCheckoutState({
              status: "error",
              error: response.error,
              errorInfo: response.errorInfo,
              code: response.code,
            });
            //TODO: if closed?  get it with auth?
          }
        })
        .catch((e) => {
          console.log("test error", e, e.error, e.message);
          setCheckoutState({
            status: "error",
            error: e.message,
            errorInfo: e.errorInfo,
            code: e.code,
          });
        });
    },
    [setCheckoutState]
  );

  const getCheckout = useCallback(
    (request: GetCheckoutRequest) => {
      setCheckoutState((checkout) => ({ ...checkout, status: "loading" }));

      return CheckoutApi.getCheckout(request).then((response) => {
        if (response.success) {
          if (response.data.checkout.status === CHECKOUT_STATUS.FINISHED) {
            setCheckoutState({
              status: "redirect-success",
              data: response.data,
            });
          } else {
            setCheckoutState({ status: "ready", data: response.data });
          }
        } else {
          setCheckoutState({
            status: "error",
            error: response.error,
            errorInfo: response.errorInfo,
            code: response.code,
          });
        }
        return response;
      });
    },
    [setCheckoutState]
  );

  return [checkoutState, patchCheckout, getCheckout] as const;
};
